import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from '@caruso/ui';
import { Save, MessageSquare, Mail, CreditCard, Eye, EyeOff, Package, Link2, Unlink, Loader2, Check, RefreshCw } from 'lucide-react';
import { api } from '@/lib/axios';
import { cn } from '@caruso/ui';

const integrationsSchema = z.object({
  whatsapp: z.object({
    evolutionApiUrl: z.string().url('URL inválida').optional().or(z.literal('')),
    evolutionApiKey: z.string().optional(),
    evolutionInstance: z.string().optional(),
    whatsappEnabled: z.boolean().optional(),
  }),
  email: z.object({
    sendgridApiKey: z.string().optional(),
    emailFrom: z.string().email('Email inválido').optional().or(z.literal('')),
    emailFromName: z.string().optional(),
    emailEnabled: z.boolean().optional(),
  }),
  payment: z.object({
    asaasApiKey: z.string().optional(),
    asaasApiUrl: z.string().url('URL inválida').optional().or(z.literal('')),
    asaasEnabled: z.boolean().optional(),
  }),
});

type IntegrationsFormData = z.infer<typeof integrationsSchema>;

interface BlingConfig {
  enabled: boolean;
  syncProducts: boolean;
  syncOrders: boolean;
  defaultDeposit: string | null;
  connected: boolean;
}

interface BlingDeposit {
  id: number;
  descricao: string;
  situacao?: boolean;
  padrao?: boolean;
}

export function IntegrationsPage() {
  const { toast } = useToast();
  const [showWhatsAppKey, setShowWhatsAppKey] = useState(false);
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [showPaymentKey, setShowPaymentKey] = useState(false);
  const [isConnectingBling, setIsConnectingBling] = useState(false);

  const { data: integrations, isLoading, refetch } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await api.get('/settings/integrations');
      return response.data;
    },
  });

  // Bling configuration
  const { data: blingConfig, refetch: refetchBling } = useQuery<BlingConfig>({
    queryKey: ['bling-config'],
    queryFn: async () => {
      const response = await api.get('/bling/config');
      return response.data;
    },
  });

  // Bling deposits (only fetch when connected)
  const { data: blingDeposits } = useQuery<{ data: BlingDeposit[] }>({
    queryKey: ['bling-deposits'],
    queryFn: async () => {
      const response = await api.get('/bling/deposits');
      return response.data;
    },
    enabled: blingConfig?.connected === true,
  });

  // Handle OAuth callback from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const blingCode = urlParams.get('code');
    const blingState = urlParams.get('state');

    if (blingCode && blingState) {
      // Process Bling OAuth callback
      handleBlingCallback(blingCode);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleBlingCallback = async (code: string) => {
    try {
      setIsConnectingBling(true);
      await api.post('/bling/auth/callback', {
        code,
        redirectUri: `${window.location.origin}/configuracoes/integracoes`,
      });
      toast({
        title: 'Bling conectado',
        description: 'Integracao com o Bling realizada com sucesso!',
        variant: 'success',
      });
      refetchBling();
    } catch (error: any) {
      toast({
        title: 'Erro ao conectar',
        description: error.response?.data?.message || 'Falha na autenticacao com o Bling',
        variant: 'destructive',
      });
    } finally {
      setIsConnectingBling(false);
    }
  };

  const connectBlingMutation = useMutation({
    mutationFn: async () => {
      const redirectUri = `${window.location.origin}/configuracoes/integracoes`;
      const response = await api.get('/bling/auth/authorize', {
        params: { redirect_uri: redirectUri },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Redirect to Bling OAuth
      window.location.href = data.authUrl;
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao iniciar conexao com o Bling',
        variant: 'destructive',
      });
    },
  });

  const disconnectBlingMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/bling/disconnect');
    },
    onSuccess: () => {
      toast({
        title: 'Bling desconectado',
        description: 'A integracao com o Bling foi removida.',
        variant: 'success',
      });
      refetchBling();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao desconectar o Bling',
        variant: 'destructive',
      });
    },
  });

  const updateBlingConfigMutation = useMutation({
    mutationFn: async (config: Partial<BlingConfig>) => {
      await api.patch('/bling/config', config);
    },
    onSuccess: () => {
      toast({
        title: 'Configuracoes salvas',
        description: 'As configuracoes do Bling foram atualizadas.',
        variant: 'success',
      });
      refetchBling();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Falha ao atualizar configuracoes',
        variant: 'destructive',
      });
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IntegrationsFormData>({
    resolver: zodResolver(integrationsSchema),
    values: integrations || {
      whatsapp: {},
      email: {},
      payment: {},
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: IntegrationsFormData) => {
      const response = await api.put('/settings/integrations', data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Integrações atualizadas',
        description: 'Configurações salvas com sucesso!',
        variant: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: IntegrationsFormData) => {
    // Remove keys que não foram alteradas (ainda mostram **CONFIGURED**)
    const cleanData = { ...data };

    if (cleanData.whatsapp?.evolutionApiKey === '***CONFIGURED***') {
      delete cleanData.whatsapp.evolutionApiKey;
    }
    if (cleanData.email?.sendgridApiKey === '***CONFIGURED***') {
      delete cleanData.email.sendgridApiKey;
    }
    if (cleanData.payment?.asaasApiKey === '***CONFIGURED***') {
      delete cleanData.payment.asaasApiKey;
    }

    mutation.mutate(cleanData);
  };

  const whatsappEnabled = watch('whatsapp.whatsappEnabled');
  const emailEnabled = watch('email.emailEnabled');
  const paymentEnabled = watch('payment.asaasEnabled');

  if (isLoading) {
    return <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-serif text-brand-gold">Integrações</h1>
        <p className="text-brand-ivory/60 mt-2">
          Configure as integrações com serviços externos
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* WhatsApp Integration */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <MessageSquare className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-brand-gold">WhatsApp (Evolution API)</CardTitle>
                  <CardDescription className="text-brand-ivory/60">
                    Configuração para envio de mensagens via WhatsApp
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="whatsapp-enabled"
                  checked={whatsappEnabled}
                  onChange={(e) => setValue('whatsapp.whatsappEnabled', e.target.checked)}
                  className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold focus:ring-2"
                />
                <Label htmlFor="whatsapp-enabled" className="text-brand-ivory text-sm cursor-pointer">
                  Habilitado
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="evolutionApiUrl" className="text-brand-ivory">
                URL da API Evolution
              </Label>
              <Input
                id="evolutionApiUrl"
                {...register('whatsapp.evolutionApiUrl')}
                placeholder="https://sendwpp.twepulse.com"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                disabled={!whatsappEnabled}
              />
              {errors.whatsapp?.evolutionApiUrl && (
                <p className="text-xs text-red-400">{errors.whatsapp.evolutionApiUrl.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="evolutionInstance" className="text-brand-ivory">
                Nome da Instância
              </Label>
              <Input
                id="evolutionInstance"
                {...register('whatsapp.evolutionInstance')}
                placeholder="minha-instancia"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                disabled={!whatsappEnabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="evolutionApiKey" className="text-brand-ivory">
                API Key
              </Label>
              <div className="relative">
                <Input
                  id="evolutionApiKey"
                  type={showWhatsAppKey ? 'text' : 'password'}
                  {...register('whatsapp.evolutionApiKey')}
                  placeholder="Deixe em branco para manter a atual"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-10"
                  disabled={!whatsappEnabled}
                />
                <button
                  type="button"
                  onClick={() => setShowWhatsAppKey(!showWhatsAppKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-ivory"
                >
                  {showWhatsAppKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-brand-ivory/40">
                {integrations?.whatsapp?.evolutionApiKey === '***CONFIGURED***'
                  ? 'API Key configurada. Deixe em branco para manter a atual.'
                  : 'Insira a API Key da Evolution API'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email Integration */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-brand-gold">Email (SendGrid)</CardTitle>
                  <CardDescription className="text-brand-ivory/60">
                    Configuração para envio de emails
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="email-enabled"
                  checked={emailEnabled}
                  onChange={(e) => setValue('email.emailEnabled', e.target.checked)}
                  className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold focus:ring-2"
                />
                <Label htmlFor="email-enabled" className="text-brand-ivory text-sm cursor-pointer">
                  Habilitado
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emailFrom" className="text-brand-ivory">
                Email Remetente
              </Label>
              <Input
                id="emailFrom"
                type="email"
                {...register('email.emailFrom')}
                placeholder="noreply@example.com"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                disabled={!emailEnabled}
              />
              {errors.email?.emailFrom && (
                <p className="text-xs text-red-400">{errors.email.emailFrom.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailFromName" className="text-brand-ivory">
                Nome do Remetente
              </Label>
              <Input
                id="emailFromName"
                {...register('email.emailFromName')}
                placeholder="Minha Empresa"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                disabled={!emailEnabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sendgridApiKey" className="text-brand-ivory">
                SendGrid API Key
              </Label>
              <div className="relative">
                <Input
                  id="sendgridApiKey"
                  type={showEmailKey ? 'text' : 'password'}
                  {...register('email.sendgridApiKey')}
                  placeholder="Deixe em branco para manter a atual"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-10"
                  disabled={!emailEnabled}
                />
                <button
                  type="button"
                  onClick={() => setShowEmailKey(!showEmailKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-ivory"
                >
                  {showEmailKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-brand-ivory/40">
                {integrations?.email?.sendgridApiKey === '***CONFIGURED***'
                  ? 'API Key configurada. Deixe em branco para manter a atual.'
                  : 'Insira a API Key do SendGrid'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Payment Integration */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <CreditCard className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <CardTitle className="text-brand-gold">Pagamentos (ASAAS)</CardTitle>
                  <CardDescription className="text-brand-ivory/60">
                    Configuração para gateway de pagamento
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="payment-enabled"
                  checked={paymentEnabled}
                  onChange={(e) => setValue('payment.asaasEnabled', e.target.checked)}
                  className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold focus:ring-2"
                />
                <Label htmlFor="payment-enabled" className="text-brand-ivory text-sm cursor-pointer">
                  Habilitado
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="asaasApiUrl" className="text-brand-ivory">
                URL da API ASAAS
              </Label>
              <Input
                id="asaasApiUrl"
                {...register('payment.asaasApiUrl')}
                placeholder="https://sandbox.asaas.com/api/v3"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                disabled={!paymentEnabled}
              />
              {errors.payment?.asaasApiUrl && (
                <p className="text-xs text-red-400">{errors.payment.asaasApiUrl.message}</p>
              )}
              <p className="text-xs text-brand-ivory/40">
                Use sandbox para testes: https://sandbox.asaas.com/api/v3
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asaasApiKey" className="text-brand-ivory">
                ASAAS API Key
              </Label>
              <div className="relative">
                <Input
                  id="asaasApiKey"
                  type={showPaymentKey ? 'text' : 'password'}
                  {...register('payment.asaasApiKey')}
                  placeholder="Deixe em branco para manter a atual"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-10"
                  disabled={!paymentEnabled}
                />
                <button
                  type="button"
                  onClick={() => setShowPaymentKey(!showPaymentKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-ivory"
                >
                  {showPaymentKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-brand-ivory/40">
                {integrations?.payment?.asaasApiKey === '***CONFIGURED***'
                  ? 'API Key configurada. Deixe em branco para manter a atual.'
                  : 'Insira a API Key do ASAAS'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <Button type="submit" variant="premium" disabled={mutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {mutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </form>

      {/* Bling ERP Integration - Outside the form since it has its own OAuth flow */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Package className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-brand-gold">Bling ERP</CardTitle>
                <CardDescription className="text-brand-ivory/60">
                  Integracao com o sistema Bling para gestao de produtos, pedidos e NF-e
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {blingConfig?.connected ? (
                <span className="flex items-center gap-1 text-sm text-green-400">
                  <Check className="w-4 h-4" />
                  Conectado
                </span>
              ) : (
                <span className="text-sm text-brand-ivory/50">Desconectado</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!blingConfig?.connected ? (
            <div className="text-center py-4">
              <p className="text-brand-ivory/60 mb-4 text-sm">
                Conecte sua conta do Bling para sincronizar produtos, pedidos e emitir NF-e diretamente pelo CRM.
              </p>
              <Button
                variant="premium"
                onClick={() => connectBlingMutation.mutate()}
                disabled={connectBlingMutation.isPending || isConnectingBling}
              >
                {connectBlingMutation.isPending || isConnectingBling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Conectar Bling
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
              {/* Sync Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-brand-ivory">Sincronizar Produtos</Label>
                    <p className="text-xs text-brand-ivory/50">
                      Importar e exportar produtos entre o CRM e o Bling
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={blingConfig?.syncProducts ?? true}
                    onChange={(e) =>
                      updateBlingConfigMutation.mutate({ syncProducts: e.target.checked })
                    }
                    className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold focus:ring-2"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-brand-ivory">Sincronizar Pedidos</Label>
                    <p className="text-xs text-brand-ivory/50">
                      Enviar pedidos do CRM para o Bling automaticamente
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={blingConfig?.syncOrders ?? true}
                    onChange={(e) =>
                      updateBlingConfigMutation.mutate({ syncOrders: e.target.checked })
                    }
                    className="w-4 h-4 text-brand-gold bg-brand-smoke border-brand-gold/20 rounded focus:ring-brand-gold focus:ring-2"
                  />
                </div>

                {/* Default Deposit */}
                {blingDeposits?.data && blingDeposits.data.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-brand-ivory">Deposito Padrao</Label>
                    <select
                      value={blingConfig?.defaultDeposit || ''}
                      onChange={(e) =>
                        updateBlingConfigMutation.mutate({ defaultDeposit: e.target.value || undefined })
                      }
                      className={cn(
                        'w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg',
                        'text-brand-ivory focus:outline-none focus:border-brand-gold/30'
                      )}
                    >
                      <option value="">Selecione um deposito</option>
                      {blingDeposits.data.map((deposit) => (
                        <option key={deposit.id} value={deposit.id}>
                          {deposit.descricao} {deposit.padrao && '(Padrao)'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-brand-ivory/40">
                      Deposito usado para atualizacoes de estoque
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-brand-gold/10">
                <Button
                  variant="ghost"
                  onClick={() => refetchBling()}
                  className="text-brand-ivory/70 hover:text-brand-gold"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar Status
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => disconnectBlingMutation.mutate()}
                  disabled={disconnectBlingMutation.isPending}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  {disconnectBlingMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <Unlink className="w-4 h-4 mr-2" />
                      Desconectar
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
